import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Check, Crown, Flame, Loader2 } from "lucide-react";
import { toast } from "sonner";
import useSubscription from "../hooks/useSubscription";
import BrandWordmark from "../components/BrandWordmark";

const FEATURES_FREE = [
  "3 saved workouts",
  "1 gym buddy",
  "30-day session history",
  "Plan up to 14 days ahead",
  "Full exercise library (64+)",
  "Active workout + rest timer",
];
const FEATURES_PRO = [
  "Unlimited workouts & buddies",
  "Unlimited calendar planning",
  "Buddy challenges & bragging rights",
  "Coach Hub (Train with Grizz)",
  "PDF progress reports",
  "Trainer / doctor share links",
  "AI 3D clip-art image gen",
  "Priority new features",
];

export default function Pricing() {
  const nav = useNavigate();
  const { sub } = useSubscription();
  const [loading, setLoading] = useState(null);

  const checkout = async (pkg) => {
    setLoading(pkg);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: pkg,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="border-b border-neutral-900">
        <div className="max-w-3xl mx-auto p-4 sm:p-6 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-white">
            <Flame className="w-5 h-5 text-blue-500" />
            <BrandWordmark size="sm" />
          </Link>
          <button onClick={() => nav(-1)} className="flex items-center text-neutral-400 hover:text-blue-500 text-xs uppercase tracking-widest">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold mb-2">UNLOCK PRO</div>
        <h1 className="font-display text-5xl sm:text-6xl uppercase leading-none mb-2">Train with no limits</h1>
        <p className="text-neutral-400 max-w-xl">Free forever to start. Go Pro when you're ready to push past the caps — only A$5 a month.</p>

        {sub?.is_pro && (
          <div className="mt-5 border border-blue-500 p-4 flex items-center gap-3">
            <Crown className="w-6 h-6 text-blue-500" />
            <div className="flex-1">
              <div className="font-display text-xl uppercase">You're Pro</div>
              <div className="text-xs text-neutral-400">
                {sub.is_scholarship ? "Scholarship — full access, no charge." : sub.in_trial ? `Trial ends in ${sub.days_left} days` : `Renews in ${sub.days_left} days`}
              </div>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4 mt-7">
          {/* FREE */}
          <div className="border border-neutral-800 p-5" data-testid="plan-free">
            <div className="text-xs tracking-widest uppercase text-neutral-500">Free</div>
            <div className="font-display text-4xl mt-1">A$0</div>
            <div className="text-xs text-neutral-500">forever</div>
            <ul className="mt-5 space-y-2">
              {FEATURES_FREE.map((f) => (
                <li key={f} className="text-sm text-neutral-300 flex items-start gap-2">
                  <Check className="w-4 h-4 text-neutral-500 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Button disabled className="w-full bg-neutral-800 text-neutral-400 rounded-none uppercase tracking-wider font-display h-10 mt-6 cursor-default">Current</Button>
          </div>

          {/* PRO */}
          <div className="border-2 border-blue-500 p-5 relative" data-testid="plan-pro">
            <div className="absolute -top-3 left-4 px-2 py-0.5 bg-blue-500 text-white text-[10px] tracking-widest uppercase font-bold">Most Popular</div>
            <div className="flex items-center gap-2"><Crown className="w-4 h-4 text-blue-500" /><div className="text-xs tracking-widest uppercase text-blue-500 font-bold">Pro</div></div>
            <div className="font-display text-4xl mt-1">A$5<span className="text-base text-neutral-500"> /month</span></div>
            <div className="text-xs text-neutral-400">or A$50/year — save A$10</div>
            <ul className="mt-5 space-y-2">
              {FEATURES_PRO.map((f) => (
                <li key={f} className="text-sm text-white flex items-start gap-2">
                  <Check className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <div className="flex gap-2 mt-6">
              <Button data-testid="checkout-monthly-btn" onClick={() => checkout("monthly")} disabled={loading === "monthly"} className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-10">
                {loading === "monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Monthly"}
              </Button>
              <Button data-testid="checkout-yearly-btn" onClick={() => checkout("yearly")} disabled={loading === "yearly"} variant="outline" className="flex-1 bg-transparent border-blue-500 text-white rounded-none uppercase tracking-wider font-display h-10">
                {loading === "yearly" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yearly"}
              </Button>
            </div>
            <div className="text-xs text-neutral-500 text-center mt-2">7-day free trial included on signup. Cancel anytime.</div>
          </div>
        </div>

        <div className="mt-8 text-xs text-neutral-500 leading-relaxed">
          <p className="mb-2"><span className="text-neutral-300 font-semibold">Secure payments by Stripe.</span> Test mode — use any 4242 4242 4242 4242 card with any future expiry & CVC.</p>
          <p>Need help, billing questions, or a scholarship? <span className="text-blue-500">support@gainstracker.com</span></p>
        </div>
      </div>
    </div>
  );
}
